import React from "react";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { FormattedMessage } from "react-intl";
import { IconWrapper } from "../utils/IconWrapper";
import { formatLink } from "../../utils/functions";

interface FormTextAreaProps {
  id: string;
  placeholder?: string;
  question: string;
  divider?: string;
  value?: string;
  inputStyle?: string;
  divStyle?: string;
  labelStyle?: string;
  dividerHeaderStyle?: string;
  repeatable?: boolean;
  containerStyle?: string;
  register?: any;
  errors?: Record<string, Object>;
  link?: string | boolean;
  cursiveLabel?: string;
  repeatableHeader?: string;
  repeatableKey?: { key: string; last: boolean; amount: number };
  index?: number;
  overlayId?: string;
  disabled?: boolean;
  overlayToggle?: (id: string) => void;
  changeAmount?: (key: string, direction: number) => void;
  validate?: (id: string) => void;
  handleInputChange: (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    id: string,
    type?: string
  ) => void;
}

export const FormTextArea: React.FC<FormTextAreaProps> = ({
  id,
  placeholder,
  handleInputChange,
  question,
  divider,
  value,
  inputStyle,
  divStyle,
  repeatableHeader,
  changeAmount,
  index,
  overlayToggle,
  labelStyle,
  containerStyle,
  dividerHeaderStyle,
  repeatable,
  register,
  errors,
  link,
  cursiveLabel,
  repeatableKey,
  validate,
  overlayId,
  disabled,
}) => {
  const styles = inputStyle
    ? inputStyle
    : "font-semibold text-brand-dark_blue focus:outline-none focus:bg-blue-100 focus:shadow-inner border border-gray-300 rounded-lg py-2 px-4 m-4 appearance-none leading-normal cursor-pointer";

  const divStyles = divStyle
    ? divStyle
    : "flex flex-col mb-4 xl:w-11/12 xl:m-auto";

  const labelStyles = labelStyle
    ? labelStyle
    : "m-2 ml-4 uppercase font-bold text-lg text-brand-dark_blue";

  const linkStyles =
    "hover:text-brand-dark_blue text-brand-blue mb-2 uppercase font-bold text-lg";

  const participantId = repeatable ? id.split("_")[1] : ""; //expected id is 'BLABLABLA_2' => 2

  const dividerHeaderStyles = dividerHeaderStyle
    ? dividerHeaderStyle
    : "m-2 uppercase font-bold text-lg text-brand-blue";

  const dividerStyles = divider ? divider : "";
  const containerStyles = containerStyle ? containerStyle : "";

  const errorDiv = errors ? (
    errors[id] ? (
      <p className='text-red-400 ml-4'>
        {" "}
        <FormattedMessage id={errors[id]["message"]} />
      </p>
    ) : null
  ) : null;

  const requiredAsterisk =
    register === null || register === undefined ? null : (
      <span className='font-normal text-red-500'>*</span>
    );

  const mouseHoverTrigger = (e: any) => {
    overlayToggle(overlayId);
  };

  const helpIcon = overlayId ? (
    <IconWrapper styles={"self-center"}>
      <IoIosHelpCircleOutline
        onMouseEnter={(e) => mouseHoverTrigger(e)}
        onMouseLeave={(e) => mouseHoverTrigger(e)}
        size={30}
      ></IoIosHelpCircleOutline>
    </IconWrapper>
  ) : null;

  const labelTag =
    typeof link === "string" ? (
      <span className={`${linkStyles}`}>
        <a href={formatLink(link)} target='_blank' rel='noreferrer'>
          {question}
          {requiredAsterisk}
        </a>
      </span>
    ) : question && overlayId ? (
      <div className='flex relative'>
        <span className={`${labelStyles}`}>
          {question}
          {requiredAsterisk}
        </span>
        {helpIcon}
      </div>
    ) : question ? (
      <span className={`${labelStyles}`}>
        {question}
        {requiredAsterisk}
      </span>
    ) : null;

  const additionalLabel = cursiveLabel ? (
    <p className='text-brand-dark_blue italic ml-4'>{cursiveLabel}</p>
  ) : null;

  const buttonStyle =
    "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-auto";
  const hideDecreaseRepeatableStyle = repeatableKey
    ? Number(repeatableKey.amount) === 1
      ? "invisible focus:outline-none outline-none"
      : "visible"
    : null;
  const lastOfRepeatGroup = repeatableKey
    ? repeatableKey.last
      ? true
      : false
    : false;
  const repeatableButtons = lastOfRepeatGroup ? (
    <div className='flex flex-row justify-between'>
      <button
        onClick={() => changeAmount(repeatableKey.key, 1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_add' /> {repeatableHeader.toLowerCase}
      </button>
      <button
        onClick={() => changeAmount(repeatableKey.key, -1)}
        type='button'
        className={`${buttonStyle} ${hideDecreaseRepeatableStyle}`}
      >
        <FormattedMessage id='Form.Repeat_remove' />{" "}
        {repeatableHeader.toLowerCase}
      </button>
    </div>
  ) : null;

  return (
    <div
      className={`${divStyles} ${dividerStyles} ${containerStyles}`}
      tabIndex={index}
    >
      {divider && repeatable ? (
        <span className={`${dividerHeaderStyles}`}>
          {repeatableHeader} {participantId}
        </span>
      ) : null}
      {labelTag}
      {additionalLabel}
      <textarea
        value={value}
        className={`${styles} ${disabled ? "bg-gray-200" : ""}`}
        name={id}
        placeholder={placeholder}
        onChange={(e) => handleInputChange(e, id)}
        ref={register}
        onBlur={validate ? () => validate(id) : null}
        disabled={disabled ? true : false}
      />
      {errorDiv}
      {repeatableButtons}
    </div>
  );
};

export default FormTextArea;
